import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useLayoutEffect,
  useImperativeHandle
} from 'react'

import useResizeObserver from '@react-hook/resize-observer'

import { useVTK } from '../../hooks'

import { VTKViewerProps } from './VTKViewer.types'

import base64toBlob from '../../lib/base64toBlob'

const useSize = (target: React.MutableRefObject<HTMLDivElement>) => {
  const [size, setSize] = useState<DOMRect>()

  useLayoutEffect(() => {
    setSize(target.current.getBoundingClientRect())
  }, [target])

  useResizeObserver(target, (entry) => setSize(entry.contentRect))
  return size
}

// eslint-disable-next-line react/display-name
export const VTKViewer = React.forwardRef<any, VTKViewerProps>(({ 
  setViewerState, 
  style, 
  children 
}, ref) => {

  const canvasContainer = useRef<any>(null)

  const size = useSize(canvasContainer)

  const { context, loadFile, state, dispatch, fastLoading } = useVTK(canvasContainer)

  // this is pretty cool, can be used to control viewer from external component
  useImperativeHandle(ref, () => {
    return {
      dispatch,
      handleScreenshot,
      loadFile,
      fastLoading
    }
  })

  // TODO: this doesn't look good yet 
  // toggle branding style on canvas resize
  // const widthRef = useRef(900)
  // useEffect(() => {
  //   if(!size) return
  //   if(size.width < 900 && widthRef.current >= 900) {
  //     dispatch({
  //       type: 'toggle-branding-size',
  //       value: false
  //     })
  //   } else if(size.width >= 900 && widthRef.current < 900) {
  //     dispatch({
  //       type: 'toggle-branding-size',
  //       value: true
  //     })
  //   }
  //   widthRef.current = size.width
  // }, [dispatch, size])

  // passes state back to parent component
  useEffect(() => {
    if (!setViewerState) return
    setViewerState(state)
  }, [setViewerState, state])

  // runs on component resize
  useEffect(() => {
    if (!context.current) return
    const { renderWindow } = context.current

    const dims = canvasContainer.current.getBoundingClientRect()

    // this happens while resizing
    if (dims.height + dims.width === 0) return

    const devicePixelRatio = window.devicePixelRatio || 1

    renderWindow.getViews().forEach((view: any) => {
      view.setSize(
        Math.floor(dims.width * devicePixelRatio),
        Math.floor(dims.height * devicePixelRatio)
      )
    })

  }, [context, size])

  // this could be implemented in useVTK
  const handleScreenshot = useCallback(async (filename?: string, clipboard?:boolean) => {
    const now = new Date()
    const fileName = typeof filename === 'string' ? filename : `Pollination_screenshot_${now.getHours()}_${now.getMinutes()}_${now.getSeconds()}`

    // @ts-ignore
    return await context.current.renderWindow.captureImages()[0]
      .then((image: string) => {
        const a = document.createElement('a')
        document.body.appendChild(a)
        a.style.display = 'none'
        a.id = 'download'
        a.href = image
        a.download = fileName
        a.click()

        document.getElementsByTagName('BODY')[0].removeChild(a)

        // copy to clipboard on by default
        if (typeof clipboard !== 'undefined' && !clipboard) return

        const imageSource = base64toBlob(image)

        navigator.clipboard.write([
          // @ts-ignore
          new ClipboardItem({ [imageSource.type]: imageSource })
        ])
      })
  }, [context])

  // keep state in sync as user interacts with viewer
  // useEffect(() => {
  //   if (canvasContainer && canvasContainer.current) {
  //     canvasContainer.current.addEventListener('mouseup', (e) => {
  //       if (dispatch) dispatch()
  //     })
  //     canvasContainer.current.addEventListener('scroll', (e) => {
  //       if (dispatch) dispatch()
  //     })
  //   }
  // }, [dispatch])

  return (
    <div ref={canvasContainer}
      id="vtkviewer"
      style={{
        height: '100%',
        width: '100%',
        justifyContent: 'center',
        ...style
      }}
    >
      {children}
    </div>
  )
})
